import { Component, OnInit, ViewChild, ViewChildren, ElementRef, QueryList } from '@angular/core';
import { KENDO_CONVERSATIONALUI, SendMessageEvent, User, Message, ChatComponent } from '@progress/kendo-angular-conversational-ui';
import { AgentsService } from 'src/app/services/agents.service';
import { CommonModule } from '@angular/common';
import { MarkdownModule } from 'ngx-markdown';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import {
  thumbDownIcon,
  thumbUpIcon,
} from '@progress/kendo-svg-icons';
import { SessionsService } from 'src/app/services/sessions.service';
import { FeedbackService } from 'src/app/services/feedback.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { LanguageService } from 'src/app/services/language.service';
import { MenuComponent } from '@progress/kendo-angular-menu';

@Component({
    selector: 'app-home',
    imports: [
        CommonModule,
        KENDO_CONVERSATIONALUI,
        ButtonsModule,
        MarkdownModule,
        TranslateModule,
        MenuComponent
    ],
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: true
})
export class HomeComponent implements OnInit {
  readonly thumbDownIcon = thumbDownIcon;
  readonly thumbUpIcon = thumbUpIcon;

  public chatMessages: Message[] = [];

  public readonly user: User = {id: 'user'};
  public readonly agent: User = {id: 'agent', name: 'Agent'};

  public clientSessions: any[] = [];
  public menuItems: any[] = [];

  @ViewChild('kendoChat') kendoChat: ChatComponent;
  @ViewChildren('messageElement', { read: ElementRef }) messagesList!: QueryList<ElementRef>;

  constructor(private _agentsService: AgentsService, private _feedbackService: FeedbackService, private _sessionsService: SessionsService,
    private _translate: TranslateService, private _route: ActivatedRoute, private _languageService: LanguageService) {
  }

  ngOnInit(): void {
    this._route.queryParams.subscribe(params => {
      const lang = params['lang'];
      if (lang) {
        this._languageService.changeLanguage(lang);
        this._translate.get('chatPlaceholder').subscribe((res: string) => {
          if (this.kendoChat) {
            this.kendoChat.messageBox.messageBoxInput.placeholder = res;
          }
        });
      }
      if (params['test'] == 1 && window.innerWidth > 1280) {
        const container = document.getElementsByClassName('chat-container')[0] as HTMLElement;
        if (container) {
          container.style.width = '50%';
          container.style.float = 'right';
        }
      }
    })
    this.initializeSessions();
  }

  public initializeSessions() {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      this.sendFirstMessage();
      return;
    }

    this._sessionsService.getAllClientSessions(userId).subscribe({
      next: (sessions) => {
        this.clientSessions = sessions;
        const clientToken = localStorage.getItem('clientToken');

        const sessionMenuItems = this.clientSessions.map(s => {
          const lastMsg = s.conversation[s.conversation.length - 1];
          const timestamp = new Date(lastMsg.timestamp).toLocaleString(this._translate.currentLang, {
            dateStyle: 'short',
            timeStyle: 'short'
          });
          const isActive = clientToken === s.clientToken;
          return {
            text: `${isActive ? '(Active) ' : ''}Session: ${timestamp}`
          };
        });

        this.menuItems = [{
          text: '⋮',
          items: [{ text: this._translate.instant('newChat') }, ...sessionMenuItems]
        }];

        if (clientToken) {
          const activeSession = sessions.find(s => s.clientToken === clientToken);

          if (activeSession) {
            const lastMsg = activeSession.conversation[activeSession.conversation.length - 1];
            const lastMsgDate = new Date(lastMsg.timestamp);
            const twoDaysAgo = new Date();
            twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);

            if (lastMsgDate < twoDaysAgo) {
              localStorage.removeItem('clientToken');
              this.sendFirstMessage();
            } else {
              this.loadSession(activeSession.conversation);
            }
            return;
          }
        }

        this.sendFirstMessage();
      },
      error: (err) => {
        console.error('Failed to load sessions', err);
        this.sendFirstMessage();
      }
    });
  }

  public sendFirstMessage() {
    this._translate.get('firstMessage').subscribe((res: string) => {
      this.chatMessages = [{ author: this.agent, text: res, timestamp: new Date(), typing: true }];
      setTimeout(() => {
        this.chatMessages[0].typing = false;
      }, 1000);
     });
  }

  public loadSession(session: any) {
    const loadedChatMessages = session.map(m => ({
      ...m,
      author: m.author === 'user' ? this.user : this.agent,
      timestamp: new Date(
        m.timestamp.endsWith('Z') ? m.timestamp : m.timestamp + 'Z'
      )
    }));
    this._translate.get('firstMessage').subscribe((res: string) => {
      this.chatMessages = [{ author: this.agent, text: res, timestamp: new Date() }, ...loadedChatMessages];
    });
  }

  public sendMessage(message: SendMessageEvent): void {
    if (message.message.text) {
      const userMessage: Message = {
        author: this.user,
        text: message.message.text,
        timestamp: new Date()
      }
      this.chatMessages = [...this.chatMessages, userMessage];
      const agentResponse = { id: '', author: this.agent, text: '', timestamp: new Date(), typing: true };
      setTimeout(() => {
        this.chatMessages = [...this.chatMessages, agentResponse];
        this._agentsService.query(message.message.text ?? '').subscribe(response => {
          const clientToken = localStorage.getItem('clientToken');
          if (!clientToken) {
            localStorage.setItem('clientToken', response.clientToken ?? '');
            this.clientSessions.push({ clientToken: response.clientToken, conversation: response.messages });
            if (this.menuItems.length <= 0) {
              this.menuItems.push({ text: '⋮', items: [{ text: this._translate.instant('newChat') }] });
            }
            this.menuItems[0].items.push({ text: "(Active) Session: " + new Date().toLocaleString(this._translate.currentLang, { dateStyle: 'short', timeStyle: 'short' }) });
          }
          const userId = localStorage.getItem('userId');
          if (!userId) {
            localStorage.setItem('userId', response.userId ?? '');
          }
          const agentResponseRes = response.messages.filter(m => m.author === 'agent')[0];
          const clientQuestion = response.messages.filter(m => m.author === 'user')[0];

          this.clientSessions.find(s => s.clientToken === response.clientToken).conversation.push(clientQuestion);
          this.clientSessions.find(s => s.clientToken === response.clientToken).conversation.push(agentResponseRes);

          agentResponse.id = agentResponseRes.id;
          agentResponse.timestamp = new Date(agentResponseRes.timestamp);
          agentResponse.typing = false;
          agentResponse.text = agentResponseRes.text;

          this.scrollToNewestMessage();
        });
      }, 1000)
    }
  }

  public sendFeedback(messageId: string, type: 'positive' | 'negative'): void {
    this._feedbackService.sendFeedback(messageId, type).subscribe({
      next: (res) => {
        this.chatMessages.filter(m => m['id'] === messageId)[0]['feedbackType'] = type;
        this.clientSessions.forEach(s => {
          const conversation = s.conversation.find(m => m.id === messageId);
          if (conversation) {
            conversation.feedbackType = type;
          }
        });
      },
      error: (err) => console.log(err),
    });
  }

  public onSessionSelect(event: any) {
    if (String(event.index) === '0' || event.item.text?.toLowerCase()?.includes('active')) return;

    // New Chat
    if (event.index === '0_0') {
      this.kendoChat.messageBox.messageBoxInput.disabled = false;
      localStorage.removeItem('clientToken');
      this.chatMessages = [];
      this.initializeSessions();
      return;
    }

    // Load session
    const index = Number(event.index.split('_')[1]);
    this.loadSession(this.clientSessions[index - 1].conversation);
    if (localStorage.getItem('clientToken') === this.clientSessions[index - 1].clientToken) {
      this.kendoChat.messageBox.messageBoxInput.disabled = false;
      return;
    }
    this.kendoChat.messageBox.messageBoxInput.disabled = true;
  }

  scrollToNewestMessage(): void {
    setTimeout(() => {
        if (this.messagesList && this.messagesList.length > 0) {
            const newestMessageElement = this.messagesList.last.nativeElement;
            newestMessageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, 10);
  }
}
